<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Head, Link, usePage, router } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import CarCard from '@/Components/Front/CarCard.vue';

const props = defineProps({
    setting: Object,
    mobils: Array,
    // SEO & Page Info
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_image: String,
    // Filter Context
    filter_type: String,
    jenisMobil: Object, // If viewing by Type
    popularJenis: Array,
    // Global Data
    kecamatans: Array, // For Footer/Area Layanan if needed, or just remove if not used in this page
    // Note: features, ulasans, news might not be passed to this page by default controller, check controller
});

// WA Logic
const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20sewa%20mobil`);
const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

// Helper for SEO
const pageTitle = computed(() => props.title || 'Daftar Armada - ' + (props.setting?.setting_title || 'Gotransport'));
const pageDesc = computed(() => props.meta_description || 'Daftar armada rental mobil terlengkap di Banyuwangi');

// Mobile Menu
const isMobileMenuOpen = ref(false);
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Filter State
const selectedType = ref('all');
const selectedDriver = ref('all');
const selectedPrice = ref('all');

// Dropdown State
const isOpenType = ref(false);
const isOpenPrice = ref(false);

const toggleType = () => {
    isOpenType.value = !isOpenType.value;
    isOpenPrice.value = false;
};

const togglePrice = () => {
    isOpenPrice.value = !isOpenPrice.value;
    isOpenType.value = false;
};

// Close dropdown handler
const closeDropdowns = (e) => {
    if (!e.target.closest('.custom-dropdown')) {
        isOpenType.value = false;
        isOpenPrice.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', closeDropdowns);
});

onUnmounted(() => {
    document.removeEventListener('click', closeDropdowns);
});

// Initialize filters from server context
if (props.filter_type === 'jenis_slug' && props.jenisMobil) {
    selectedType.value = props.jenisMobil.jenis_slug;
} else if (props.filter_type === 'include_driver') {
    selectedDriver.value = 'include_driver';
} else if (props.filter_type === 'lepas_kunci') {
    selectedDriver.value = 'lepas_kunci';
}

// Extract Unique Types for Sidebar
const uniqueTypes = computed(() => {
    if (!props.mobils) return [];
    // Group by jenis_nama
    const groups = {};
    props.mobils.forEach(m => {
        if (m.jenis_mobil) {
            const slug = m.jenis_mobil.jenis_slug; // Assuming slug exists in relation or we derive it? 
            // In blade: data-type="{{ Str::slug($jenisNama) }}"
            // Check if backend sends jenis_slug in relationship. Usually yes.
            // If not, we might need to rely on ID or Name. 
            // Let's assume we can use name and slugify it if needed, or just check what data we have.
            // For safety, let's use jenis_nama as key if slug is missing, but slug is better for URLs.
            // Controller: with('jenisMobil').
            const name = m.jenis_mobil.jenis_nama;
            const key = slug || name.toLowerCase().replace(/ /g, '-');
            
            if (!groups[key]) {
                groups[key] = {
                    name: name,
                    slug: key,
                    count: 0
                };
            }
            groups[key].count++;
        }
    });
    return Object.values(groups);
});

// Computed Filtered List
const filteredMobils = computed(() => {
    let list = props.mobils || [];

    return list.filter(mobil => {
        // 1. Type Filter
        if (selectedType.value !== 'all') {
            const typeSlug = mobil.jenis_mobil?.jenis_slug || mobil.jenis_mobil?.jenis_nama?.toLowerCase().replace(/ /g, '-');
            if (typeSlug !== selectedType.value) return false;
        }

        // 2. Driver Filter
        if (selectedDriver.value !== 'all') {
             const fasilitas = (mobil.mobil_fasilitas || '').toLowerCase();
             const hasInclude = (mobil.mobil_harga_include > 0) || fasilitas.includes('include');
             // Logic from blade for lepas kunci: has 'lepas kunci' OR (NOT include AND something else?) 
             // Blade: $isLepasKunci = str_contains($fasilitas, 'lepas kunci') || !str_contains($fasilitas, 'include');
             const hasLepasKunci = fasilitas.includes('lepas kunci') || !fasilitas.includes('include');
             
             if (selectedDriver.value === 'include_driver' && !hasInclude) return false;
             if (selectedDriver.value === 'lepas_kunci' && !hasLepasKunci) return false;
        }

        // 3. Price Filter
        if (selectedPrice.value !== 'all') {
            const [min, max] = selectedPrice.value.split('-').map(Number);
            let hasMatchingPrice = false;

            // Check Lepas Kunci Price if relevant (Driver Filter is 'all' or 'lepas_kunci')
            if ((selectedDriver.value === 'all' || selectedDriver.value === 'lepas_kunci') && mobil.mobil_harga_sewa > 0) {
                if (mobil.mobil_harga_sewa >= min && mobil.mobil_harga_sewa <= max) {
                    hasMatchingPrice = true;
                }
            }

            // Check With Driver Price if relevant (Driver Filter is 'all' or 'include_driver')
            // Only check if we haven't found a match yet
            if (!hasMatchingPrice && (selectedDriver.value === 'all' || selectedDriver.value === 'include_driver') && mobil.mobil_harga_include > 0) {
                if (mobil.mobil_harga_include >= min && mobil.mobil_harga_include <= max) {
                    hasMatchingPrice = true;
                }
            }
            
            if (!hasMatchingPrice) return false;
        }
        
        return true;
    });
});

const resetFilters = () => {
    // If we are on a server-filtered page (e.g. /mobil/jenis/...), navigate to main page
    if (props.filter_type) {
        router.visit('/mobil');
        return;
    }
    
    // Otherwise just reset client-side state
    selectedType.value = 'all';
    selectedDriver.value = 'all';
    selectedPrice.value = 'all';
};

// Counts for sidebar badges
const getCountByType = (slug) => {
    // Basic implementation: count within the FULL list (not filtered by other filters, to mimic simplified behavior OR filtered?)
    // Blade does simple grouping.
    // Let's filter the MAIN props.mobils to give accurate counts for "if I click this".
    return props.mobils.filter(m => {
        const typeSlug = m.jenis_mobil?.jenis_slug || m.jenis_mobil?.jenis_nama?.toLowerCase().replace(/ /g, '-');
        return typeSlug === slug;
    }).length;
};

// Helper for 'lepas kunci' count
const lepasKunciCount = computed(() => {
    return (props.mobils || []).filter(m => {
        const f = (m.mobil_fasilitas || '').toLowerCase();
        return f.includes('lepas kunci') || !f.includes('include');
    }).length;
});

const includeDriverCount = computed(() => {
    return (props.mobils || []).filter(m => {
         const f = (m.mobil_fasilitas || '').toLowerCase();
         return (m.mobil_harga_include > 0) || f.includes('include');
    }).length;
});

// Title for Hero based on filter
const heroTitle = computed(() => {
    if (props.filter_type === 'jenis_slug' && props.jenisMobil) return `Armada ${props.jenisMobil.jenis_nama}`;
    if (props.filter_type === 'jenis') return 'Armada Berdasarkan Jenis';
    if (props.filter_type === 'include_driver') return 'Armada Dengan Sopir';
    if (props.filter_type === 'lepas_kunci') return 'Armada Lepas Kunci';
    return 'Armada Kami';
});
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="pageTitle" 
        :metaDescription="pageDesc"
        :og_title="props.og_title || pageTitle"
        :og_description="props.og_description || pageDesc"
        :og_image="props.og_image"
        :meta_keywords="meta_keywords"
    >
        <Head>

            <meta name="description" :content="pageDesc" />
            <meta v-if="meta_keywords" name="keywords" :content="meta_keywords" />
            <meta property="og:title" :content="props.og_title || pageTitle" />
            <meta property="og:description" :content="props.og_description || pageDesc" />
            <meta v-if="props.og_image" property="og:image" :content="props.og_image" />
            <meta v-if="props.og_image" property="og:image:width" content="1200" />
            <meta v-if="props.og_image" property="og:image:height" content="630" />
            <meta v-if="props.og_image" property="og:image:type" content="image/jpeg" />
        </Head>

        <!-- Armada Hero Section -->
        <section class="relative overflow-hidden">
             <!-- Background with Orange Gradient (Matching Homepage) -->
             <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>
             
             <!-- Decorative Elements -->
             <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <svg class="absolute right-0 top-0 text-white/10 w-64 h-64 -mr-16 -mt-16" fill="currentColor" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50"/></svg>
                <div class="absolute left-10 bottom-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
             </div>

             <div class="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 text-center py-12 md:py-16">
                 <!-- Breadcrumb (Moved to Top) -->
                 <div class="flex justify-center mb-6">
                     <div class="inline-flex items-center gap-2 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/20 shadow-sm">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <i class="fa fa-chevron-right text-[10px] opacity-70"></i>
                        <span class="text-white font-bold">{{ heroTitle }}</span>
                     </div>
                 </div>

                 <h1 class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-sm">{{ heroTitle }}</h1>
                 <p class="text-amber-100 text-lg max-w-2xl mx-auto font-medium">{{ props.meta_description || 'Pilihan kendaraan terbaik untuk kebutuhan transportasi Anda di Banyuwangi' }}</p>
             </div>
             
             <!-- Bottom Wave/Shape (Optional, simple curve) -->
             <div class="absolute bottom-0 left-0 right-0 text-gray-50 dark:text-gray-900 transition-colors duration-300">
                 <svg viewBox="0 0 1440 48" fill="currentColor" class="w-full h-auto block">
                     <path d="M0,48 L1440,48 L1440,0 C1440,0 1140,48 720,48 C300,48 0,0 0,0 L0,48 Z"></path>
                 </svg>
             </div>
        </section>

        <!-- Content Section -->
        <section class="py-8 md:py-12 bg-gray-50 dark:bg-gray-900 min-h-[600px] transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                
                <!-- Filter Horizontal Bar (Compact & Custom UI) -->
                <div class="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 p-2 mb-6 sticky top-20 z-30 transform transition-all rounded-lg">
                    <!-- Desktop: Single Row | Mobile: Stacked -->
                    <div class="flex flex-col lg:flex-row gap-2 items-center justify-between text-sm">
                        
                        <!-- Main Filters Row -->
                        <div class="flex flex-col md:flex-row items-center gap-2 w-full lg:w-auto">
                            <!-- Custom Dropdown: Jenis -->
                            <div class="relative custom-dropdown w-full md:w-auto z-20">
                                <button 
                                    class="flex items-center justify-between gap-2 rounded-lg px-3 py-2 border transition-all font-medium text-sm w-full md:w-48 lg:w-40 xl:w-48"
                                    :class="selectedType !== 'all' ? 'bg-orange-50 border-orange-500 text-orange-700' : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-orange-200 hover:bg-orange-50 dark:hover:bg-gray-600'"
                                >
                                    <div class="flex items-center gap-2 truncate">
                                        <i class="fa fa-car flex-shrink-0" :class="selectedType !== 'all' ? 'text-orange-600' : 'text-orange-500'"></i>
                                        <span class="truncate">
                                            {{ selectedType === 'all' ? 'Semua Jenis' : uniqueTypes.find(t => t.slug === selectedType)?.name || 'Pilih Jenis' }}
                                        </span>
                                    </div>
                                    <i class="fa fa-chevron-down text-xs ml-1 transition-transform duration-200 flex-shrink-0" :class="[{ 'rotate-180': isOpenType }, selectedType !== 'all' ? 'text-orange-600' : 'text-gray-400']"></i>
                                </button>
                                <!-- Dropdown Menu -->
                                <div 
                                    v-show="isOpenType"
                                    class="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-all animate-fade-in-up"
                                >
                                    <div class="p-1 max-h-60 overflow-y-auto custom-scrollbar">
                                        <button 
                                            @click="selectedType = 'all'; isOpenType = false"
                                            class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-orange-50 dark:hover:bg-gray-700 hover:text-orange-600 transition-colors"
                                            :class="selectedType === 'all' ? 'bg-orange-50 dark:bg-gray-700 text-orange-600 font-bold' : 'text-gray-600 dark:text-gray-300'"
                                        >
                                            Semua Jenis
                                        </button>
                                        <button 
                                            v-for="type in uniqueTypes" 
                                            :key="type.slug"
                                            @click="selectedType = type.slug; isOpenType = false"
                                            class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-orange-50 dark:hover:bg-gray-700 hover:text-orange-600 transition-colors flex justify-between items-center"
                                            :class="selectedType === type.slug ? 'bg-orange-50 dark:bg-gray-700 text-orange-600 font-bold' : 'text-gray-600 dark:text-gray-300'"
                                        >
                                            <span class="truncate pr-2">{{ type.name }}</span>
                                            <span class="text-xs bg-gray-100 dark:bg-gray-900 px-1.5 py-0.5 rounded text-gray-500 dark:text-gray-400 flex-shrink-0">{{ type.count }}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Custom Buttons: Driver -->
                            <div class="flex p-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 w-full md:w-auto">
                                <button @click="selectedDriver = 'all'" :class="['flex-1 md:flex-none px-4 py-2 rounded-md text-xs font-bold border transition-all shadow-sm whitespace-nowrap', selectedDriver === 'all' ? 'bg-gray-700 dark:bg-gray-900 text-white border-gray-700 dark:border-gray-900 shadow-md transform scale-105' : 'bg-transparent text-gray-500 dark:text-gray-300 border-transparent hover:text-gray-700 dark:hover:text-white shadow-none']">Semua</button>
                                <button @click="selectedDriver = 'lepas_kunci'" :class="['flex-1 md:flex-none px-4 py-2 rounded-md text-xs font-bold border transition-all shadow-sm whitespace-nowrap', selectedDriver === 'lepas_kunci' ? 'bg-orange-500 text-white border-orange-500 shadow-md transform scale-105' : 'bg-transparent text-gray-500 dark:text-gray-300 border-transparent hover:text-gray-700 dark:hover:text-white shadow-none']">Lepas Kunci</button>
                                <button @click="selectedDriver = 'include_driver'" :class="['flex-1 md:flex-none px-4 py-2 rounded-md text-xs font-bold border transition-all shadow-sm whitespace-nowrap', selectedDriver === 'include_driver' ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105' : 'bg-transparent text-gray-500 dark:text-gray-300 border-transparent hover:text-gray-700 dark:hover:text-white shadow-none']">Dengan Sopir</button>
                            </div>

                            <!-- Custom Dropdown: Price -->
                            <div class="relative custom-dropdown w-full md:w-auto z-10">
                                <button 
                                    @click="togglePrice"
                                    class="flex items-center justify-between gap-2 rounded-lg px-3 py-2 border transition-all font-medium text-sm w-full md:w-48 lg:w-40 xl:w-48"
                                    :class="selectedPrice !== 'all' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-green-200 hover:bg-green-50 dark:hover:bg-gray-600'"
                                >
                                    <div class="flex items-center gap-2 truncate">
                                        <i class="fa fa-tag flex-shrink-0" :class="selectedPrice !== 'all' ? 'text-green-600' : 'text-green-500'"></i>
                                        <span class="truncate">
                                            {{ 
                                                selectedPrice === 'all' ? 'Semua Harga' : 
                                                selectedPrice === '0-500000' ? '< Rp 500rb' :
                                                selectedPrice === '500000-1000000' ? 'Rp 500rb - 1 Jt' : '> Rp 1 Jt'
                                            }}
                                        </span>
                                    </div>
                                    <i class="fa fa-chevron-down text-xs ml-1 transition-transform duration-200 flex-shrink-0" :class="[{ 'rotate-180': isOpenPrice }, selectedPrice !== 'all' ? 'text-green-600' : 'text-gray-400']"></i>
                                </button>
                                <!-- Dropdown Menu -->
                                <div 
                                    v-show="isOpenPrice"
                                    class="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-all animate-fade-in-up"
                                >
                                    <div class="p-1">
                                            <button @click="selectedPrice = 'all'; isOpenPrice = false" class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-gray-700 hover:text-green-600 transition-colors" :class="selectedPrice === 'all' ? 'bg-green-50 dark:bg-gray-700 text-green-600 font-bold' : 'text-gray-600 dark:text-gray-300'">Semua Harga</button>
                                            <button @click="selectedPrice = '0-500000'; isOpenPrice = false" class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-gray-700 hover:text-green-600 transition-colors" :class="selectedPrice === '0-500000' ? 'bg-green-50 dark:bg-gray-700 text-green-600 font-bold' : 'text-gray-600 dark:text-gray-300'">&lt; Rp 500rb</button>
                                            <button @click="selectedPrice = '500000-1000000'; isOpenPrice = false" class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-gray-700 hover:text-green-600 transition-colors" :class="selectedPrice === '500000-1000000' ? 'bg-green-50 dark:bg-gray-700 text-green-600 font-bold' : 'text-gray-600 dark:text-gray-300'">Rp 500rb - 1 Jt</button>
                                            <button @click="selectedPrice = '1000000-9999999'; isOpenPrice = false" class="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-gray-700 hover:text-green-600 transition-colors" :class="selectedPrice === '1000000-9999999' ? 'bg-green-50 dark:bg-gray-700 text-green-600 font-bold' : 'text-gray-600 dark:text-gray-300'">&gt; Rp 1 Jt</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Reset -->
                        <div class="mt-2 lg:mt-0 w-full lg:w-auto flex justify-end">
                            <button @click="resetFilters" class="text-xs text-red-500 hover:text-red-600 font-bold flex items-center gap-1.5 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap">
                                <i class="fa fa-refresh"></i> Reset Filter
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Full Width Grid -->
                <div>
                        <!-- Results Count -->


                        <div v-if="filteredMobils.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                             <div v-for="mobil in filteredMobils" :key="mobil.id" class="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-orange-400 hover:shadow-xl transition-all duration-300 flex flex-col">
                                <!-- Gambar Mobil -->
                                <div class="relative aspect-[4/3] bg-white dark:bg-gray-700/50 overflow-hidden border-b border-gray-100 dark:border-gray-700">
                                    <!-- Custom Pill for Type (Tag) -->
                                    <div v-if="mobil.jenis_mobil" class="absolute top-4 left-4 z-10">
                                        <span class="bg-blue-600 text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                                            <i class="fa fa-tag text-[9px]"></i> {{ mobil.jenis_mobil.jenis_nama }}
                                        </span>
                                    </div>

                                    <img :src="`/storage/mobil/${mobil.mobil_foto}`" :alt="mobil.mobil_nama" class="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                </div>

                                <!-- Info Mobil -->
                                <div class="p-4 flex flex-col flex-grow bg-white dark:bg-gray-800 transition-colors">
                                    <!-- Title -->
                                    <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-1 truncate group-hover:text-blue-700 transition-colors" title="{{ mobil.mobil_nama }}">
                                        <Link :href="`/mobil/${mobil.mobil_slug}`">{{ mobil.mobil_nama }}</Link>
                                    </h3>
                                    
                                    <!-- Description -->
                                    <p class="text-gray-500 text-xs mb-4 line-clamp-2 leading-relaxed min-h-[2.5em]">
                                        {{ (mobil.mobil_deskripsi || 'Cocok untuk perjalanan dinas, wisata keluarga, atau wedding car di Banyuwangi.').replace(/<[^>]*>?/gm, '') }}
                                    </p>

                                    <!-- Price List (Homepage Style) -->
                                    <div class="mb-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 rounded-xl overflow-hidden mt-auto">
                                        <div v-if="mobil.mobil_harga_sewa" class="flex items-center justify-between text-sm p-3">
                                            <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                                                <span class="w-6 h-6 bg-orange-100 dark:bg-gray-600 flex items-center justify-center text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-gray-500 rounded-lg"><i class="fa fa-key text-xs"></i></span>
                                                Lepas Kunci
                                            </span>
                                            <span class="font-bold text-gray-900 dark:text-white">Rp {{ fmt(mobil.mobil_harga_sewa) }}</span>
                                        </div>
                                        <div v-if="mobil.mobil_harga_include" class="flex items-center justify-between text-sm p-3">
                                            <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                                                <span class="w-6 h-6 bg-blue-100 dark:bg-gray-600 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-gray-500 rounded-lg"><i class="fa fa-user text-xs"></i></span>
                                                Dengan Sopir
                                            </span>
                                            <span class="font-bold text-gray-900 dark:text-white">Rp {{ fmt(mobil.mobil_harga_include) }}</span>
                                        </div>
                                    </div>

                                    <!-- Buttons -->
                                    <div class="grid grid-cols-2 gap-3 pt-2">
                                        <Link :href="`/mobil/${mobil.mobil_slug}`" class="flex items-center justify-center px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-bold transition-all rounded-lg">
                                            Detail <i class="fa fa-arrow-right ml-2 text-xs"></i>
                                        </Link>
                                        <a :href="waUrl" target="_blank" class="flex items-center justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-bold transition-all rounded-lg shadow-md hover:shadow-lg">
                                            <i class="fa fa-whatsapp mr-1.5 text-lg"></i> Booking
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                            <div class="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i class="fa fa-search text-gray-300 dark:text-gray-500 text-3xl"></i>
                            </div>
                            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Tidak ada armada ditemukan</h3>
                            <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">Kriteria filter yang Anda pilih tidak cocok dengan armada manapun yang tersedia saat ini.</p>
                            <button @click="resetFilters" class="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-bold transition-colors shadow-lg shadow-orange-500/30">
                                Reset Filter
                            </button>
                        </div>
                        <!-- Results Count (Moved to Bottom) -->
                        <div class="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                            <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white">Total Armada</h2>
                            <span class="text-xs md:text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1 rounded-full shadow-sm w-fit">
                                Menampilkan <strong>{{ filteredMobils.length }}</strong> dari {{ props.mobils?.length || 0 }} armada
                            </span>
                        </div>
                </div>
            </div>
        </section>

        <!-- Informasi Jenis Kendaraan Section -->
        <section v-if="popularJenis && popularJenis.length > 0" class="py-16 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center max-w-3xl mx-auto mb-12">
                     <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
                        <i class="fa fa-newspaper-o text-blue-600"></i> Informasi Jenis Kendaraan
                     </h2>
                     <p class="text-gray-500 dark:text-gray-400">Pelajari lebih dalam tentang setiap jenis kendaraan untuk memilih yang terbaik sesuai kebutuhan Anda</p>
                     <div class="w-20 h-1 bg-blue-500 mx-auto mt-4 rounded-full"></div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                     <div v-for="jenis in popularJenis" :key="jenis.id" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-lg transition-all duration-300 flex flex-col group">
                         <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 transition-colors">{{ jenis.jenis_nama }}</h3>
                         
                         <div class="text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed flex-grow prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:my-2 dark:prose-invert" v-if="jenis.jenis_deskripsi" v-html="jenis.jenis_deskripsi"></div>
                         <p v-else class="text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed flex-grow">Pilihan kendaraan terbaik untuk perjalanan Anda di Banyuwangi.</p>

                         <div class="grid grid-cols-2 gap-3 mt-auto">
                             <Link :href="`/mobil/jenis/${jenis.jenis_slug}`" class="flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 text-sm font-bold rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors">
                                 <span>Armada</span>
                                 <span class="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full">{{ jenis.mobils_count }}</span>
                             </Link>
                             <Link :href="`/mobil/jenis/${jenis.jenis_slug}/artikel`" class="flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors">
                                 <i class="fa fa-file-text-o mr-2"></i> Artikel
                             </Link>
                         </div>
                     </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
